import { ProductType } from '@core/models/api/dsm-types';
import { ProductsSelectors } from '@core/store/selectors';
import { createSelector } from '@ngrx/store';
import { GeneralUtils } from '@shared/utils/general.utils';
import { getCoveredLocationEntity } from '../../covered-location/covered-location.selector';
import { getAllFeatureFlags } from '../../feature-flag/feature-flag.selector';
import { getMortgagesOfMortgageType } from '../../mortgage/mortgage.selector';
import { TaskModel } from '../task.model';
import { getIncompleteTasks, selectAllTasks } from './task.selector';
import { TaskUtils } from '../utils/task.util';
import { PropertyTaskUtils } from '../utils/property-task.utils';
import {
  getBillToEscrowProducts,
  getPolicyDateOptions,
  getProduct,
  getSelectedActiveProductTypes,
  getSelectedProductTypes,
} from '@entities/product/product.selectors';
import { getMortgageState } from '@entities/has-mortgage/has-mortgage.selector';

export const getCoveredLocationTasks = (productType: ProductType) =>
  createSelector(
    selectAllTasks,
    (tasks) =>
      tasks.filter(
        (t) =>
          t?.entityType === 'coveredLocation' && t?.productType === productType
      ) as TaskModel[]
  );

export const generateCoveredLocationTasks = (productType: ProductType) =>
  createSelector(
    getSelectedProductTypes,
    ProductsSelectors.getProduct(productType),
    getCoveredLocationEntity(productType),
    getMortgageState,
    getMortgagesOfMortgageType(productType, 'Mortgagee_Ext'),
    getAllFeatureFlags,
    getPolicyDateOptions('Homeowner'),
    getSelectedActiveProductTypes,
    getBillToEscrowProducts,
    (
      selectedProductTypes,
      product,
      coveredLocation,
      hasMortgage,
      mortgages,
      featureFlags,
      policyDateOptions,
      selectedProducts,
      billToEscrowProducts
    ): TaskModel[] => {
      const isProductSelected = selectedProductTypes.includes(productType);
      const isProductValid = !GeneralUtils.isEmpty(product);
      const isCoveredLocationValid =
        coveredLocation && Object.keys(coveredLocation).length > 1;

      if (!isProductSelected || !isProductValid || !isCoveredLocationValid) {
        return [];
      }

      return PropertyTaskUtils.generatePropertyTasks(
        coveredLocation,
        featureFlags,
        selectedProducts,
        policyDateOptions,
        product,
        mortgages,
        billToEscrowProducts,
        hasMortgage
      );
    }
  );

export const updateCoveredLocationTasks = (productType: ProductType) =>
  createSelector(
    getCoveredLocationTasks(productType),
    generateCoveredLocationTasks(productType),
    (tasks, newTasks) => TaskUtils.createUpdatedTaskObject(newTasks, tasks)
  );

export const getPropertyTasks = createSelector(
  getIncompleteTasks,
  (tasks) =>
    tasks.filter((t) => t?.entityType === 'coveredLocation') as TaskModel[]
);

// Needs to know which product is being tasked
export const generatePropertyTasks = (productType: ProductType) =>
  createSelector(
    getProduct(productType),
    getCoveredLocationEntity(productType),
    getMortgageState,
    getMortgagesOfMortgageType(productType, 'Mortgagee_Ext'),
    getAllFeatureFlags,
    getPolicyDateOptions(productType),
    getSelectedActiveProductTypes,
    getBillToEscrowProducts,
    (
      product,
      coveredLocation,
      hasMortgage,
      mortgages,
      features,
      policyDateOptions,
      selectedProducts,
      billToEscrowProducts
    ) =>
      !GeneralUtils.isEmpty(product) &&
      coveredLocation &&
      Object.keys(coveredLocation).length > 1
        ? PropertyTaskUtils.generatePropertyTasks(
            coveredLocation,
            features,
            selectedProducts,
            policyDateOptions,
            product,
            mortgages,
            billToEscrowProducts,
            hasMortgage
          )
        : []
  );

export const updatePropertyTasks = (productType: ProductType) =>
  createSelector(
    getPropertyTasks,
    generatePropertyTasks(productType),
    (tasks, newTasks) => TaskUtils.createUpdatedTaskObject(newTasks, tasks)
  );
